import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from 'react';
import updateFilterLegStates from '../../Filters/state/updateFilterLegStates';
import { useOrderFilterTagsActions } from 'clientState/providers/OrderedFilterTagsProvider';

const ALIAS = 'timeRanges';
const TimeFilterContext = createContext([]);

const INITIAL_TIME_RANGE = { min: 0, max: 24 };

const getNextTimeValue = (value, defaultValue) =>
  Number.isFinite(value) ? value : defaultValue;

const applyLegFilterReducer = (departureTimeRanges, { legId, timeRange }) => {
  const currentTimeRange = departureTimeRanges[legId] || INITIAL_TIME_RANGE;
  const nextTimeRange = {
    min: getNextTimeValue(timeRange.min, currentTimeRange.min),
    max: getNextTimeValue(timeRange.max, currentTimeRange.max),
    id: legId,
  };
  return updateFilterLegStates([legId], nextTimeRange, departureTimeRanges);
};

const reducer = (departureTimeRanges, action) => {
  switch (action.type) {
    case 'RESET':
      return [];
    case 'FILTER':
      return applyLegFilterReducer(departureTimeRanges, action);
    default:
      return departureTimeRanges;
  }
};

function areTimeRangesDifferent(timeRange, timeRange2) {
  return timeRange.min !== timeRange2.min || timeRange.max !== timeRange2.max;
}

export const useTimeLegFilter = (legId) => {
  const [departureTimeRanges, dispatch] = useContext(TimeFilterContext);
  const { add, remove } = useOrderFilterTagsActions();
  const legTimeRange = departureTimeRanges[legId] || INITIAL_TIME_RANGE;
  const applyFilter = useCallback(
    (timeRange) => {
      dispatch({
        type: 'FILTER',
        legId,
        timeRange,
      });
      if (areTimeRangesDifferent(timeRange, INITIAL_TIME_RANGE)) {
        add({
          alias: ALIAS,
          legId,
          undo: () => {
            dispatch({ type: 'FILTER', legId, timeRange: INITIAL_TIME_RANGE });
            remove({ alias: ALIAS, legId });
          },
        });
      } else {
        remove({ alias: ALIAS, legId });
      }
    },
    [dispatch, legId, add, remove]
  );
  return [legTimeRange, applyFilter];
};

export const useAppliedTimeFilter = () => {
  const [timeRanges] = useContext(TimeFilterContext);
  return timeRanges?.some(
    (timeRange) => timeRange && (timeRange.min > 0 || timeRange.max < 24)
  );
};

export const useTimeFilter = () => {
  const [timeRanges, dispatch] = useContext(TimeFilterContext);
  const { clearAllStartsWith } = useOrderFilterTagsActions();

  const resetAll = useCallback(() => {
    dispatch({ type: 'RESET' });
    clearAllStartsWith(ALIAS);
  }, [dispatch, clearAllStartsWith]);
  return [timeRanges, resetAll];
};

const TimeFilterProvider = ({ children }) => {
  const stateAndDispatch = useReducer(reducer, []);

  return (
    <TimeFilterContext.Provider value={stateAndDispatch}>
      {children}
    </TimeFilterContext.Provider>
  );
};

export default TimeFilterProvider;
